import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DotsThreeVertical } from 'phosphor-react';
import { SearchField } from '../SearchField';
import { Skeleton } from '../Skeleton';
import { ChannelFilter } from './ChannelFilter';

type ChannelType = 'all' | 'whatsapp' | 'email';

interface ThreadListProps {
  title: string;
  threads: React.ReactNode;
  searchValue: string;
  onSearchChange: (val: string) => void;
  actions?: React.ReactNode;
  isLoading?: boolean;
  isEmpty?: boolean;
  emptyState?: React.ReactNode;
  onMenuButtonClick?: () => void;
  menuRef?: React.RefObject<HTMLDivElement | null>;
  showMenu?: boolean;
  isProcessingBulkAction?: boolean;
  // Channel filtering
  showChannelFilter?: boolean;
  activeChannel?: ChannelType;
  onChannelChange?: (channel: ChannelType) => void;
  whatsappCount?: number;
  emailCount?: number;
}

export const ThreadList: React.FC<ThreadListProps> = ({
  title,
  threads,
  searchValue,
  onSearchChange,
  actions,
  isLoading = false,
  isEmpty = false,
  emptyState,
  onMenuButtonClick,
  menuRef,
  showMenu = false,
  isProcessingBulkAction = false,
  showChannelFilter = false,
  activeChannel = 'all',
  onChannelChange,
  whatsappCount = 0,
  emailCount = 0
}) => {
  return (
    <>
      {/* Title row with search and actions all inline */}
      <div className="bg-muted px-3 py-3 shrink-0 sticky top-0 z-0 border-b border-border/50">
        <div className="flex items-center gap-2 w-full max-w-[1280px] mx-auto">
          <h1 className="font-sans font-semibold text-lg text-text tracking-[-0.16px] shrink-0">
            {title}
          </h1>

          {/* Search field - flex 1 to take remaining space */}
          <div className="flex-1 min-w-0">
            <SearchField
              placeholder="Buscar..."
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>

          {/* Menu button - minimal styling */}
          {onMenuButtonClick && (
            <div className="relative shrink-0" ref={menuRef}>
              <button
                onClick={onMenuButtonClick}
                disabled={isProcessingBulkAction}
                className="p-1 hover:bg-black/5 rounded transition-colors disabled:opacity-30"
                aria-label="Opciones"
              >
                <DotsThreeVertical size={18} weight="bold" className="text-text" />
              </button>
              {showMenu && actions && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute right-0 mt-1 w-64 bg-background border border-border rounded-lg shadow-lg z-50"
                >
                  {actions}
                </motion.div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Channel Filter */}
      {showChannelFilter && onChannelChange && (
        <ChannelFilter
          activeChannel={activeChannel}
          onChannelChange={onChannelChange}
          whatsappCount={whatsappCount}
          emailCount={emailCount}
        />
      )}

      {/* Threads List - clean scroll area */}
      <div className="flex-1 flex flex-col relative w-full min-h-0 overflow-y-auto">
        {isLoading ? (
          <div className="flex flex-col gap-2 w-full p-2">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="p-3 w-full">
                <Skeleton className="h-20 w-full rounded-lg" />
              </div>
            ))}
          </div>
        ) : isEmpty ? (
          emptyState
        ) : (
          <div className="flex flex-col w-full divide-y divide-border bg-background">
            <AnimatePresence>
              {threads}
            </AnimatePresence>
          </div>
        )}
      </div>
    </>
  );
};

export default ThreadList;
